<template>
  <div class="invoices wf-page">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">Form Builder</h3>
                <p class="text-muted mb-2">
                  Buat dan kelola formulir untuk mengumpulkan data pelanggan.
                </p>
                <ul class="breadcrumb">
                  <li class="breadcrumb-item">
                    <router-link to="/index">Dashboard</router-link>
                  </li>
                  <li class="breadcrumb-item active">Form Builder</li>
                </ul>
              </div>
              <div class="col-auto float-right ml-auto wf-header-actions">
                <router-link
                  v-if="!isQuotaFull"
                  :to="{ name: 'waajo_forms_create' }"
                  class="btn add-btn btn-rounded wf-primary-btn"
                  title="Buka halaman persiapan pembuatan form"
                >
                  <i class="fa fa-plus"></i>
                  Buat Form
                </router-link>
                <button
                  v-else
                  type="button"
                  class="btn add-btn btn-rounded wf-primary-btn"
                  disabled
                  title="Kuota form sudah penuh. Hapus form draft atau nonaktif terlebih dahulu."
                >
                  <i class="fa fa-plus"></i>
                  Buat Form
                </button>
              </div>
            </div>
          </div>

          <div class="card wf-toolbar-card">
            <div class="card-body">
              <div class="row align-items-end">
                <div class="col-lg-7 mb-3 mb-lg-0">
                  <label class="wf-filter-label" for="wf-form-search">
                    Cari form
                  </label>
                  <div class="wf-search-wrapper">
                    <i class="fa fa-search wf-search-icon" aria-hidden="true"></i>
                    <input
                      id="wf-form-search"
                      v-model="filters.search"
                      type="text"
                      class="form-control wf-search-input"
                      placeholder="Cari berdasarkan nama form..."
                      autocomplete="off"
                      @input="onSearchInput"
                      @keyup.enter="applySearchNow"
                    />
                    <button
                      v-if="hasSearchKeyword"
                      type="button"
                      class="wf-search-clear"
                      title="Hapus kata kunci pencarian"
                      aria-label="Hapus kata kunci pencarian"
                      @click="clearSearch"
                    >
                      <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                  <small class="text-muted">
                    Hasil pencarian diperbarui otomatis saat Anda mengetik.
                  </small>
                </div>
                <div class="col-lg-5">
                  <div class="wf-quota">
                    <div class="wf-quota-head">
                      <span class="wf-quota-label">
                        <i class="fa fa-layer-group" aria-hidden="true"></i>
                        Kuota Form
                      </span>
                      <span class="wf-quota-value">{{ quotaText }}</span>
                    </div>
                    <div
                      v-if="maxForms !== null && Number(maxForms) > 0"
                      class="wf-quota-progress"
                      role="progressbar"
                      :aria-valuenow="quotaUsed === null ? 0 : quotaUsed"
                      aria-valuemin="0"
                      :aria-valuemax="maxForms"
                    >
                      <div
                        class="wf-quota-progress-bar"
                        :style="{ width: quotaPercent + '%' }"
                      ></div>
                    </div>
                    <small v-if="isQuotaFull" class="wf-quota-warning">
                      Kuota form penuh. Hapus form draft atau nonaktif untuk
                      membebaskan slot.
                    </small>
                    <small v-else class="text-muted">
                      Form yang dihapus akan membebaskan kuota.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card wf-list-card">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between flex-wrap mb-3"
              >
                <div>
                  <h5 class="mb-0">Daftar Form</h5>
                  <small v-if="!listError && (!loading || rows.length)" class="text-muted">
                    {{ totalRecords }} form ditemukan
                    <span v-if="loading && rows.length" class="wf-loading-text">
                      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                      Memuat...
                    </span>
                  </small>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="loading"
                  @click="loadList"
                >
                  <i
                    class="fa fa-refresh"
                    :class="{ 'fa-spin': loading }"
                    aria-hidden="true"
                  ></i>
                  Muat Ulang
                </button>
              </div>

              <div
                v-if="listError"
                class="alert alert-danger d-flex align-items-center justify-content-between flex-wrap"
                role="alert"
              >
                <span>
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                  {{ listErrorMessage }}
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="loading"
                  @click="loadList"
                >
                  Coba Lagi
                </button>
              </div>

              <div v-if="loading && !rows.length" class="wf-skeleton">
                <Skeleton :count="6" height="52px" gap="10px" />
              </div>

              <div
                v-else-if="!loading && !rows.length && !listError"
                class="wf-empty-state"
              >
                <div class="wf-empty-icon">
                  <i
                    :class="hasSearchKeyword ? 'fa fa-search' : 'fa fa-wpforms'"
                    aria-hidden="true"
                  ></i>
                </div>
                <h5>
                  {{
                    hasSearchKeyword
                      ? "Form tidak ditemukan"
                      : "Belum ada form"
                  }}
                </h5>
                <p class="text-muted">
                  {{
                    hasSearchKeyword
                      ? "Coba gunakan kata kunci lain atau atur ulang pencarian."
                      : "Buat form pertama Anda untuk mulai mengumpulkan data pelanggan."
                  }}
                </p>
                <button
                  v-if="hasSearchKeyword"
                  type="button"
                  class="btn btn-outline-secondary"
                  @click="clearSearch"
                >
                  Atur Ulang Pencarian
                </button>
                <router-link
                  v-else-if="!isQuotaFull"
                  :to="{ name: 'waajo_forms_create' }"
                  class="btn wf-primary-btn"
                >
                  <i class="fa fa-plus"></i>
                  Buat Form
                </router-link>
              </div>

              <div v-else-if="rows.length" class="table-responsive">
                <table class="table table-hover custom-table mb-0 wf-table">
                  <thead>
                    <tr>
                      <th>Nama Form</th>
                      <th>Deskripsi</th>
                      <th>Status</th>
                      <th class="text-center">Versi</th>
                      <th>Terakhir Diubah</th>
                      <th class="text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in rows" :key="row.id">
                      <td>
                        <router-link
                          class="wf-form-name"
                          :to="editRoute(row)"
                          :title="row.name || ''"
                        >
                          {{ row.name || "-" }}
                        </router-link>
                        <router-link
                          :to="{
                            name: 'waajo_forms_responses',
                            params: { id: row.id }
                          }"
                          class="wf-form-results-link"
                          title="Lihat hasil pengisian form"
                        >
                          <i class="fa fa-bar-chart" aria-hidden="true"></i>
                          Lihat Hasil
                        </router-link>
                      </td>
                      <td>
                        <span
                          class="wf-description"
                          :title="row.description || ''"
                        >
                          {{ row.description || "-" }}
                        </span>
                      </td>
                      <td>
                        <span
                          class="wf-status-badge"
                          :class="'wf-status-' + statusTone(row.status)"
                        >
                          <span class="wf-status-dot" aria-hidden="true"></span>
                          {{ statusLabel(row.status) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="wf-version">
                          v{{ formatVersion(row.schema_version) }}
                        </span>
                      </td>
                      <td>
                        <span class="wf-date">
                          {{ formatDate(row.update_datetime) }}
                        </span>
                      </td>
                      <td class="text-right wf-actions-cell">
                        <div class="wf-row-actions">
                          <router-link
                            :to="editRoute(row)"
                            class="btn btn-sm wf-icon-btn"
                            title="Edit form"
                            aria-label="Edit form"
                          >
                            <i class="fa fa-pencil" aria-hidden="true"></i>
                          </router-link>

                          <button
                            v-if="canPublish(row)"
                            type="button"
                            class="btn btn-sm wf-icon-btn wf-icon-success"
                            :title="
                              row.status === 'inactive'
                                ? 'Publikasikan ulang form'
                                : 'Publikasikan form'
                            "
                            aria-label="Publikasikan form"
                            :disabled="!!actionLoadingId || loading"
                            @click="confirmLifecycle(row, 'publish')"
                          >
                            <i
                              :class="
                                actionLoadingId === row.id &&
                                actionLoadingType === 'publish'
                                  ? 'fa fa-spinner fa-spin'
                                  : 'fa fa-rocket'
                              "
                              aria-hidden="true"
                            ></i>
                          </button>

                          <button
                            v-if="canDeactivate(row)"
                            type="button"
                            class="btn btn-sm wf-icon-btn wf-icon-warning"
                            title="Nonaktifkan form (form aktif tidak dapat dihapus)"
                            aria-label="Nonaktifkan form"
                            :disabled="!!actionLoadingId || loading"
                            @click="confirmLifecycle(row, 'deactivate')"
                          >
                            <i
                              :class="
                                actionLoadingId === row.id &&
                                actionLoadingType === 'deactivate'
                                  ? 'fa fa-spinner fa-spin'
                                  : 'fa fa-pause'
                              "
                              aria-hidden="true"
                            ></i>
                          </button>

                          <button
                            v-if="isActive(row)"
                            type="button"
                            class="btn btn-sm wf-icon-btn"
                            title="Salin link form publik"
                            aria-label="Salin link form publik"
                            :disabled="!!actionLoadingId"
                            @click="copyPublicLink(row)"
                          >
                            <i class="fa fa-link" aria-hidden="true"></i>
                          </button>

                          <button
                            v-if="canDelete(row)"
                            type="button"
                            class="btn btn-sm wf-icon-btn wf-icon-danger"
                            title="Hapus form"
                            aria-label="Hapus form"
                            :disabled="!!actionLoadingId || loading"
                            @click="confirmDelete(row)"
                          >
                            <i
                              :class="
                                actionLoadingId === row.id &&
                                actionLoadingType === 'delete'
                                  ? 'fa fa-spinner fa-spin'
                                  : 'fa fa-trash'
                              "
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="rows.length" class="row align-items-center mt-3 wf-pagination">
                <div class="col-sm-12 col-md-6 mb-2 mb-md-0">
                  <span class="footer__row-count__label">Tampilkan</span>
                  <select
                    v-model.number="perPage"
                    class="select-to-paginasi"
                    style="width: 110px;"
                    :disabled="loading"
                    @change="onPerPageChange"
                  >
                    <option :value="10">10 / halaman</option>
                    <option :value="20">20 / halaman</option>
                    <option :value="50">50 / halaman</option>
                    <option :value="100">100 / halaman</option>
                  </select>
                  <span class="footer__row-count__label">
                    dari {{ totalRecords }} form
                  </span>
                </div>
                <div class="col-sm-12 col-md-6 text-right">
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage <= 1 || loading }"
                    @click.prevent="onPrevChange"
                  >
                    Sebelumnya
                  </a>
                  <span class="wf-page-label">Halaman</span>
                  <input
                    v-model.number="pageInput"
                    type="number"
                    min="1"
                    :max="lastPage"
                    class="wf-page-input"
                    :disabled="loading"
                    @keyup.enter="onPageChange(pageInput)"
                    @blur="onPageInputBlur"
                  />
                  <span class="wf-page-label">dari {{ lastPage }}</span>
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage >= lastPage || loading }"
                    @click.prevent="onNextChange"
                  >
                    Berikutnya
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import { Skeleton } from "vue-loading-skeleton";
import { formatLocalDate } from "@/utils/helper";
import {
  getHttpStatus,
  getWaajoFormsErrorMessage
} from "@/utils/waajoFormsError";
import {
  canDeactivateForm,
  canPublishForm,
  copyTextToClipboard,
  getFormStatusLabel,
  getFormStatusTone,
  getPublicFormUrl,
  isLifecycleConflict,
  isLifecycleRetryable
} from "@/utils/waajoFormsLifecycle";
import {
  hasValidationErrors,
  normalizeFieldFromServer,
  validateForm
} from "@/utils/waajoFormsSchema";

const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: "waajo-forms-list",
  components: {
    LayoutHeader,
    LayoutSidebar,
    Skeleton
  },
  data() {
    return {
      loading: false,
      listError: false,
      listErrorMessage: "",
      rows: [],
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      pageInput: 1,
      perPage: 20,
      filters: {
        search: ""
      },
      quotaTotal: null,
      maxForms: null,
      actionLoadingId: "",
      actionLoadingType: "",
      searchTimer: null,
      listRequestId: 0,
      quotaRequestId: 0,
      sessionExpired: false
    };
  },
  computed: {
    hasSearchKeyword() {
      return (this.filters.search || "").trim() !== "";
    },
    lastPage() {
      return Math.max(Number(this.totalPages) || 0, 1);
    },
    quotaUsed() {
      if (this.quotaTotal === null) {
        return null;
      }
      return Number(this.quotaTotal) || 0;
    },
    quotaText() {
      if (this.quotaUsed === null) {
        return this.listError ? "Kuota belum tersedia" : "Memuat kuota...";
      }
      if (this.maxForms !== null && Number(this.maxForms) > 0) {
        return this.quotaUsed + " dari " + this.maxForms + " form digunakan";
      }
      return this.quotaUsed + " form digunakan";
    },
    quotaPercent() {
      if (!this.maxForms || this.quotaUsed === null) {
        return 0;
      }
      return Math.min(
        Math.round((this.quotaUsed / Number(this.maxForms)) * 100),
        100
      );
    },
    isQuotaFull() {
      if (this.maxForms === null || this.quotaUsed === null) {
        return false;
      }
      const max = Number(this.maxForms);
      return max > 0 && this.quotaUsed >= max;
    }
  },
  created() {
    this.loadList();
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    // Abaikan respons yang masih berjalan setelah halaman ditinggalkan.
    this.listRequestId += 1;
    this.quotaRequestId += 1;
  },
  methods: {
    async loadList() {
      const requestId = ++this.listRequestId;
      const keyword = (this.filters.search || "").trim();

      this.loading = true;
      this.listError = false;
      this.listErrorMessage = "";

      try {
        const response = await this.$store.dispatch("waajoForms/listWaajoForms", {
          page: this.currentPage,
          per_page: this.perPage,
          search: keyword
        });

        if (requestId !== this.listRequestId) {
          return;
        }

        const payload = response && response.data ? response.data : {};
        this.rows = Array.isArray(payload.data) ? payload.data : [];
        this.totalRecords = Number(payload.totalRecords) || 0;
        this.totalPages = Number(payload.totalPages) || 0;

        const responsePage = Number(payload.page);
        if (responsePage > 0) {
          this.currentPage = responsePage;
        }
        const responsePerPage = Number(payload.perPage);
        if (responsePerPage > 0) {
          this.perPage = responsePerPage;
        }
        this.pageInput = this.currentPage;

        if (
          payload.maxForms !== undefined &&
          payload.maxForms !== null &&
          payload.maxForms !== ""
        ) {
          this.maxForms = Number(payload.maxForms);
        }

        // totalRecords pada request tanpa search adalah jumlah form non-deleted,
        // sehingga aman dipakai sebagai angka kuota.
        if (!keyword) {
          this.quotaTotal = this.totalRecords;
        } else if (this.quotaTotal === null) {
          this.loadQuota();
        }
      } catch (error) {
        if (requestId !== this.listRequestId) {
          return;
        }
        this.listError = true;
        this.listErrorMessage = this.getErrorMessage(
          error,
          "Daftar form belum dapat dimuat. Coba lagi."
        );
      } finally {
        if (requestId === this.listRequestId) {
          this.loading = false;
        }
      }
    },
    async loadQuota() {
      const requestId = ++this.quotaRequestId;
      try {
        const response = await this.$store.dispatch(
          "waajoForms/listWaajoForms",
          {
            page: 1,
            per_page: 1
          }
        );
        if (requestId !== this.quotaRequestId) {
          return;
        }
        const payload = response && response.data ? response.data : {};
        if (
          payload.totalRecords !== undefined &&
          payload.totalRecords !== null
        ) {
          this.quotaTotal = Number(payload.totalRecords) || 0;
        }
        if (
          payload.maxForms !== undefined &&
          payload.maxForms !== null &&
          payload.maxForms !== ""
        ) {
          this.maxForms = Number(payload.maxForms);
        }
      } catch (error) {
        // Kuota hanya informasi tambahan; kegagalan request ini tidak boleh
        // menutupi daftar form utama.
      }
    },
    onSearchInput() {
      this.currentPage = 1;
      this.pageInput = 1;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.loadList();
      }, SEARCH_DEBOUNCE_MS);
    },
    applySearchNow() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadList();
    },
    clearSearch() {
      if (!this.filters.search) {
        return;
      }
      this.filters.search = "";
      this.applySearchNow();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadList();
    },
    onPageChange(page) {
      const target = Math.min(
        Math.max(Number(page) || 1, 1),
        this.lastPage
      );
      if (target === this.currentPage) {
        this.pageInput = this.currentPage;
        return;
      }
      this.currentPage = target;
      this.pageInput = target;
      this.loadList();
    },
    onPageInputBlur() {
      const target = Math.min(
        Math.max(Number(this.pageInput) || 1, 1),
        this.lastPage
      );
      if (target !== this.currentPage) {
        this.onPageChange(target);
      } else {
        this.pageInput = this.currentPage;
      }
    },
    onPrevChange() {
      if (this.currentPage > 1 && !this.loading) {
        this.onPageChange(this.currentPage - 1);
      }
    },
    onNextChange() {
      if (this.currentPage < this.lastPage && !this.loading) {
        this.onPageChange(this.currentPage + 1);
      }
    },
    formatDate(value) {
      const unix = Number(value);
      if (!unix || !isFinite(unix) || unix <= 0) {
        return "-";
      }
      const date = new Date(unix * 1000);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return formatLocalDate(unix);
    },
    statusLabel(status) {
      return getFormStatusLabel(status);
    },
    statusTone(status) {
      return getFormStatusTone(status);
    },
    formatVersion(value) {
      if (value === undefined || value === null || value === "") {
        return "-";
      }
      const version = Number(value);
      return isFinite(version) ? version : "-";
    },
    isActive(row) {
      return row && row.status === "active";
    },
    canPublish(row) {
      return canPublishForm(row && row.status);
    },
    canDeactivate(row) {
      return canDeactivateForm(row && row.status);
    },
    canDelete(row) {
      return row && ["draft", "inactive"].indexOf(row.status) !== -1;
    },
    editRoute(row) {
      return {
        name: "waajo_forms_editor",
        params: { id: row.id || "" },
        query: { name: row.name || "" }
      };
    },
    async copyFormLink(id, name) {
      const url = getPublicFormUrl(id);
      try {
        const copied = await copyTextToClipboard(url);
        if (!copied) {
          throw new Error("copy failed");
        }
        this.$fire({
          title: "Link publik disalin",
          text:
            (name ? 'Link untuk "' + name + '":\n' : "") +
            url +
            "\n\nHalaman publik akan tersedia pada tahap berikutnya.",
          type: "success",
          timer: 3200
        });
      } catch (error) {
        this.$fire({
          title: "Gagal menyalin link",
          text: "Salin link berikut secara manual: " + url,
          type: "warning"
        });
      }
    },
    async copyPublicLink(row) {
      if (!row || !row.id || !this.isActive(row)) {
        return;
      }
      await this.copyFormLink(row.id, row.name);
    },
    getLifecycleConfirm(action, row) {
      const name = row.name || "form ini";
      if (action === "publish") {
        return {
          title: "Publikasikan form?",
          text:
            'Form "' +
            name +
            '" akan dipublikasikan dan berstatus aktif. Link publik akan dibuat dengan format /form/{formId}. Halaman publik akan tersedia pada tahap berikutnya.',
          confirmButtonText: "Ya, publikasikan"
        };
      }
      return {
        title: "Nonaktifkan form?",
        text:
          'Form "' +
          name +
          '" akan dinonaktifkan dan tidak lagi berstatus aktif. Schema dapat diedit kembali setelah form nonaktif. Data respons yang sudah tersimpan tidak dihapus.',
        confirmButtonText: "Ya, nonaktifkan"
      };
    },
    async confirmLifecycle(row, action) {
      if (this.actionLoadingId || this.loading) {
        return;
      }
      if (action === "publish" && !this.canPublish(row)) {
        this.$fire({
          title: "Form tidak dapat dipublikasikan",
          text:
            "Hanya form berstatus draft atau nonaktif yang dapat dipublikasikan.",
          type: "info",
          timer: 3200
        });
        return;
      }
      if (action === "deactivate" && !this.canDeactivate(row)) {
        this.$fire({
          title: "Form tidak dapat dinonaktifkan",
          text: "Hanya form aktif yang dapat dinonaktifkan.",
          type: "info",
          timer: 3200
        });
        return;
      }

      const config = this.getLifecycleConfirm(action, row);
      const result = await this.$fire({
        title: config.title,
        text: config.text,
        type: "question",
        showCancelButton: true,
        confirmButtonText: config.confirmButtonText,
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (!result || !result.value) {
        return;
      }

      await this.executeLifecycle(row, action);
    },
    async validatePublishPreflight(row) {
      const response = await this.$store.dispatch(
        "waajoForms/detailWaajoForm",
        { id: row.id }
      );
      const detail = response && response.data ? response.data : null;
      if (!detail) {
        this.$fire({
          title: "Form belum dapat dipublikasikan",
          text:
            "Detail form tidak tersedia. Muat ulang daftar lalu coba lagi.",
          type: "warning"
        });
        return false;
      }

      if (!canPublishForm(detail.status)) {
        await this.loadList();
        this.$fire({
          title: "Status form sudah berubah",
          text:
            "Form tidak lagi berstatus draft atau nonaktif. Daftar form telah dimuat ulang.",
          type: "warning"
        });
        return false;
      }

      const fields = Array.isArray(detail.fields)
        ? detail.fields.map(normalizeFieldFromServer)
        : [];
      const errors = validateForm({
        name: detail.name,
        description: detail.description,
        fields: fields,
        includeFields: true
      });
      if (hasValidationErrors(errors)) {
        this.$fire({
          title: "Schema form belum valid",
          text:
            "Perbaiki nama form, field, atau pilihan pada halaman Edit sebelum mempublikasikan form.",
          type: "warning"
        });
        return false;
      }
      return true;
    },
    async executeLifecycle(row, action) {
      if (this.actionLoadingId) {
        return;
      }

      const isPublish = action === "publish";
      const expectedStatus = isPublish ? "active" : "inactive";
      const dispatchName = isPublish
        ? "publishWaajoForm"
        : "deactivateWaajoForm";
      const fallback = isPublish
        ? "Form belum dapat dipublikasikan. Coba lagi."
        : "Form belum dapat dinonaktifkan. Coba lagi.";
      let shouldRetry = false;

      this.actionLoadingId = row.id;
      this.actionLoadingType = action;
      try {
        if (isPublish) {
          const validForPublish = await this.validatePublishPreflight(row);
          if (!validForPublish) {
            return;
          }
        }

        const response = await this.$store.dispatch(
          "waajoForms/" + dispatchName,
          { id: row.id }
        );
        const data = response && response.data ? response.data : null;
        const responseStatus =
          data && typeof data.status === "string" ? data.status : "";

        await this.loadList();
        if (this.hasSearchKeyword || this.listError) {
          await this.loadQuota();
        }

        const freshRow = this.rows.find(item => item.id === row.id);
        const confirmedStatus = responseStatus
          ? responseStatus
          : freshRow
          ? freshRow.status
          : "";

        if (confirmedStatus !== expectedStatus) {
          this.$fire({
            title: "Status form belum terkonfirmasi",
            text:
              "Permintaan diterima server, tetapi status terbaru form belum sesuai. Muat ulang daftar form untuk melihat status aktual.",
            type: "warning"
          });
          return;
        }

        if (isPublish) {
          const url = getPublicFormUrl(row.id);
          const result = await this.$fire({
            title: "Form berhasil dipublikasikan",
            text:
              'Form "' +
              (row.name || "ini") +
              '" sekarang aktif.\n\nLink publik:\n' +
              url +
              "\n\nHalaman publik akan tersedia pada tahap berikutnya.",
            type: "success",
            showCancelButton: true,
            confirmButtonText: "Salin Link",
            cancelButtonText: "Tutup",
            confirmButtonColor: "#FE9B44"
          });
          if (result && result.value) {
            await this.copyFormLink(row.id, row.name);
          }
        } else {
          this.$fire({
            title: "Form dinonaktifkan",
            text:
              'Form "' +
              (row.name || "ini") +
              '" sekarang nonaktif. Schema form dapat diedit kembali.',
            type: "success",
            timer: 2600
          });
        }
      } catch (error) {
        const message = this.getErrorMessage(error, fallback);
        const title = isPublish
          ? "Gagal mempublikasikan form"
          : "Gagal menonaktifkan form";

        if (isLifecycleConflict(error)) {
          await this.loadList();
          if (this.hasSearchKeyword || this.listError) {
            await this.loadQuota();
          }
          this.$fire({
            title: title,
            text: message,
            type: "error"
          });
        } else if (isLifecycleRetryable(error)) {
          const result = await this.$fire({
            title: title,
            text: message,
            type: "error",
            showCancelButton: true,
            confirmButtonText: "Coba Lagi",
            cancelButtonText: "Tutup",
            confirmButtonColor: "#FE9B44"
          });
          shouldRetry = !!(result && result.value);
        } else {
          this.$fire({
            title: title,
            text: message,
            type: "error"
          });
        }
      } finally {
        this.actionLoadingId = "";
        this.actionLoadingType = "";
      }

      if (shouldRetry) {
        await this.executeLifecycle(row, action);
      }
    },
    async confirmDelete(row) {
      if (this.actionLoadingId || this.loading) {
        return;
      }
      if (!this.canDelete(row)) {
        this.$fire({
          title: "Form tidak dapat dihapus",
          text:
            "Form aktif harus dinonaktifkan terlebih dahulu sebelum dapat dihapus.",
          type: "info",
          timer: 3200
        });
        return;
      }

      const name = row.name || "form ini";
      const result = await this.$fire({
        title: "Hapus form?",
        text:
          'Form "' +
          name +
          '" akan dihapus dari daftar. Tindakan ini tidak dapat dibatalkan.',
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (!result || !result.value) {
        return;
      }
      await this.executeDelete(row);
    },
    async executeDelete(row) {
      if (this.actionLoadingId) {
        return;
      }

      this.actionLoadingId = row.id;
      this.actionLoadingType = "delete";
      try {
        await this.$store.dispatch("waajoForms/deleteWaajoForm", {
          id: row.id
        });

        if (
          this.rows.length === 1 &&
          this.currentPage > 1
        ) {
          this.currentPage -= 1;
          this.pageInput = this.currentPage;
        }

        await this.loadList();
        if (this.hasSearchKeyword || this.listError) {
          await this.loadQuota();
        }

        this.$fire({
          title: "Berhasil",
          text: 'Form "' + (row.name || "ini") + '" berhasil dihapus.',
          type: "success",
          timer: 2200
        });
      } catch (error) {
        const message = this.getErrorMessage(
          error,
          "Form belum dapat dihapus. Coba lagi."
        );
        this.$fire({
          title: "Gagal menghapus form",
          text: message,
          type: "error"
        });
        if (isLifecycleConflict(error)) {
          await this.loadList();
        }
      } finally {
        this.actionLoadingId = "";
        this.actionLoadingType = "";
      }
    },
    getErrorMessage(error, fallback) {
      if (getHttpStatus(error) === 401) {
        this.handleSessionExpired();
      }
      return getWaajoFormsErrorMessage(error, fallback);
    },
    handleSessionExpired() {
      if (this.sessionExpired) {
        return;
      }
      this.sessionExpired = true;
      const currentPath =
        this.$route && this.$route.fullPath ? this.$route.fullPath : "/forms";
      this.$store.dispatch("login/resetToken").finally(() => {
        this.$router
          .replace({
            path: "/login",
            query: { redirect: currentPath }
          })
          .catch(() => {});
      });
    }
  }
};
</script>

<style scoped>
.wf-primary-btn {
  background-color: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.wf-primary-btn:hover,
.wf-primary-btn:focus {
  background-color: #f08a2e;
  border-color: #f08a2e;
  color: #ffffff;
}

.wf-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.wf-header-actions .btn {
  min-width: 140px;
}

.wf-toolbar-card,
.wf-list-card {
  border: 1px solid #eef1f5;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
}

.wf-filter-label {
  display: block;
  margin-bottom: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.wf-search-wrapper {
  position: relative;
}

.wf-search-input {
  height: 42px;
  padding-left: 38px;
  padding-right: 40px;
  border-color: #e2e8f0;
  border-radius: 8px;
}

.wf-search-input:focus {
  border-color: #fe9b44;
  box-shadow: 0 0 0 0.2rem rgba(254, 155, 68, 0.18);
}

.wf-search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  color: #94a3b8;
  transform: translateY(-50%);
  pointer-events: none;
}

.wf-search-clear {
  position: absolute;
  top: 50%;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #64748b;
  transform: translateY(-50%);
}

.wf-search-clear:hover,
.wf-search-clear:focus {
  background: #f1f5f9;
  color: #334155;
  outline: none;
}

.wf-quota {
  padding: 12px 14px;
  border: 1px solid #fde7d1;
  border-radius: 10px;
  background: #fffaf5;
}

.wf-quota-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 9px;
}

.wf-quota-label {
  color: #9a5b1d;
  font-size: 13px;
  font-weight: 700;
}

.wf-quota-label i {
  margin-right: 4px;
}

.wf-quota-value {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.wf-quota-progress {
  height: 8px;
  margin-bottom: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #f1e5da;
}

.wf-quota-progress-bar {
  height: 100%;
  border-radius: 999px;
  background: #fe9b44;
  transition: width 0.25s ease;
}

.wf-quota-warning {
  display: block;
  color: #c2410c;
  font-weight: 600;
}

.wf-loading-text {
  display: inline-flex;
  align-items: center;
  margin-left: 7px;
  color: #b45309;
  font-size: 12px;
}

.wf-loading-text i {
  margin-right: 4px;
}

.wf-skeleton {
  padding: 10px 0;
}

.wf-empty-state {
  padding: 48px 16px;
  text-align: center;
}

.wf-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: #fff4e8;
  color: #fe9b44;
  font-size: 28px;
}

.wf-empty-state h5 {
  margin-bottom: 8px;
  color: #334155;
}

.wf-empty-state p {
  max-width: 440px;
  margin: 0 auto 18px;
}

.wf-table {
  min-width: 860px;
}

.wf-table thead th {
  border-top: 0;
  border-bottom: 1px solid #e9edf2;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
}

.wf-table tbody td {
  border-color: #eef1f5;
  color: #334155;
  font-size: 14px;
}

.wf-form-name {
  color: #1f2937;
  font-weight: 600;
}

.wf-form-name:hover,
.wf-form-name:focus {
  color: #d97706;
  text-decoration: none;
}

.wf-form-results-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  margin-top: 5px;
  padding: 2px 8px;
  border: 1px solid #fde3c2;
  border-radius: 999px;
  background: #fff7ed;
  color: #b45309;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
  text-decoration: none;
  white-space: nowrap;
}

.wf-form-results-link:hover,
.wf-form-results-link:focus {
  border-color: #fe9b44;
  background: #fe9b44;
  color: #ffffff;
  text-decoration: none;
}

.wf-description {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  color: #64748b;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.wf-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}

.wf-status-dot {
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: currentColor;
}

.wf-status-draft {
  background: #f1f5f9;
  color: #475569;
}

.wf-status-active {
  background: #e8f8ee;
  color: #18753c;
}

.wf-status-inactive {
  background: #fff4e5;
  color: #a15c00;
}

.wf-status-unknown {
  background: #f1f5f9;
  color: #64748b;
}

.wf-version {
  display: inline-block;
  min-width: 38px;
  padding: 3px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.wf-date {
  color: #475569;
  white-space: nowrap;
}

.wf-actions-cell {
  white-space: nowrap;
}

.wf-row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.wf-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
}

.wf-icon-btn:hover,
.wf-icon-btn:focus {
  border-color: #fe9b44;
  background: #fff7ef;
  color: #d97706;
  outline: none;
}

.wf-icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.wf-icon-danger:hover,
.wf-icon-danger:focus {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #dc2626;
}

.wf-icon-success:hover,
.wf-icon-success:focus {
  border-color: #86efac;
  background: #f0fdf4;
  color: #15803d;
}

.wf-icon-warning:hover,
.wf-icon-warning:focus {
  border-color: #fcd34d;
  background: #fffbeb;
  color: #b45309;
}

.wf-action-hint {
  display: inline-flex;
  cursor: help;
}

.wf-page-label {
  margin: 0 7px;
  color: #64748b;
  font-size: 13px;
}

.wf-page-input {
  width: 58px;
  height: 34px;
  padding: 2px 6px;
  border: 1px solid #dbe2ea;
  border-radius: 6px;
  color: #334155;
  text-align: center;
}

.wf-page-input:focus {
  border-color: #fe9b44;
  outline: none;
  box-shadow: 0 0 0 0.15rem rgba(254, 155, 68, 0.18);
}

@media (max-width: 767.98px) {
  .wf-header-actions {
    width: 100%;
    margin-top: 12px;
  }

  .wf-header-actions .btn {
    width: 100%;
  }

  .wf-description {
    max-width: 190px;
  }

  .wf-pagination {
    text-align: center;
  }

  .wf-pagination .text-right {
    text-align: center !important;
  }

  .wf-page-label {
    margin: 0 4px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/forms/list.vue